import { useState } from 'react'
import { addRecipe, deleteRecipe, updateRecipe } from '../../budget/repo'
import { navigate } from '../../lib/router'
import { useStore } from '../../store'
import { Card, CommittedInput, Dialog, Empty, Sheet, TopBar } from '../../ui/parts'
import { MAX_DESCRIPTION, safeUrl, sortedRecipes, sourceLabel, type Recipe } from '../cookbook'

interface ScrapeResponse {
  title: string | null
  description: string | null
  imageUrl: string | null
}

export function CookbookScreen() {
  const { snapshot } = useStore()
  const [adding, setAdding] = useState(false)

  if (!snapshot) return null
  const recipes = sortedRecipes(snapshot.cookbook)

  return (
    <div className="screen">
      <TopBar
        title="Cookbook"
        subtitle={
          recipes.length === 0
            ? undefined
            : `${recipes.length} recipe${recipes.length === 1 ? '' : 's'}`
        }
        onBack={() => navigate('/other')}
      />

      <div className="stack">
        {recipes.length === 0 ? (
          <Empty
            title="No recipes yet"
            body="Paste a link and the title, summary and photo come across on their own."
            action={
              <button type="button" className="btn" onClick={() => setAdding(true)}>
                Add a recipe
              </button>
            }
          />
        ) : (
          recipes.map((recipe) => <RecipeCard key={recipe.id} recipe={recipe} />)
        )}
      </div>

      <button type="button" className="fab" onClick={() => setAdding(true)} aria-label="Add recipe">
        +
      </button>

      {adding ? <RecipeSheet onClose={() => setAdding(false)} /> : null}
    </div>
  )
}

function RecipeCard({ recipe }: { recipe: Recipe }) {
  const { mutate } = useStore()
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const source = recipe.url ? sourceLabel(recipe.url) : null

  return (
    <Card className="flush">
      {recipe.imageUrl ? (
        // Hotlinked from the source. If it 404s later the element hides itself rather than
        // leaving a torn-image icon in the middle of the card.
        <img
          className="recipe-image"
          src={recipe.imageUrl}
          alt=""
          loading="lazy"
          onError={(e) => {
            ;(e.currentTarget as HTMLImageElement).style.display = 'none'
          }}
        />
      ) : null}

      <div className="recipe-body">
        <div className="row between" style={{ alignItems: 'flex-start', gap: 8 }}>
          <div className="grow">
            <div className="card-title">{recipe.title}</div>
            {source ? (
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                {source}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="icon-btn"
            aria-label={`Edit ${recipe.title}`}
            onClick={() => setEditing(true)}
          >
            ✎
          </button>
          <button
            type="button"
            className="icon-btn"
            aria-label={`Remove ${recipe.title}`}
            onClick={() => setConfirmDelete(true)}
          >
            ✕
          </button>
        </div>

        {recipe.description ? (
          <div className="muted" style={{ fontSize: 14, marginTop: 6, lineHeight: 1.45 }}>
            {recipe.description}
          </div>
        ) : null}

        {recipe.url ? (
          <a
            className="btn outline small"
            style={{ marginTop: 10, display: 'inline-block' }}
            href={recipe.url}
            target="_blank"
            rel="noreferrer noopener"
          >
            Open recipe
          </a>
        ) : null}
      </div>

      {editing ? <EditSheet recipe={recipe} onClose={() => setEditing(false)} /> : null}

      {confirmDelete ? (
        <Dialog
          title={`Remove ${recipe.title}?`}
          body="It disappears from the cookbook. The original page is untouched."
          confirmLabel="Remove"
          danger
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            setConfirmDelete(false)
            void mutate((s) => deleteRecipe(s, recipe.id))
          }}
        />
      ) : null}
    </Card>
  )
}

function EditSheet({ recipe, onClose }: { recipe: Recipe; onClose: () => void }) {
  const { mutate } = useStore()

  return (
    <Sheet onClose={onClose}>
      <h2 style={{ margin: '0 0 14px', fontSize: 20 }}>Edit recipe</h2>

      <label className="field">
        <span>Title</span>
        <CommittedInput
          value={recipe.title}
          onCommit={(title) => void mutate((s) => updateRecipe(s, recipe.id, { title }))}
        />
      </label>

      <label className="field">
        <span>Description</span>
        <textarea
          defaultValue={recipe.description}
          maxLength={MAX_DESCRIPTION}
          onBlur={(e) => {
            if (e.target.value.trim() !== recipe.description) {
              void mutate((s) => updateRecipe(s, recipe.id, { description: e.target.value }))
            }
          }}
        />
      </label>

      <label className="field">
        <span>Link</span>
        <CommittedInput
          value={recipe.url}
          placeholder="https://…"
          onCommit={(url) => void mutate((s) => updateRecipe(s, recipe.id, { url }))}
        />
      </label>

      <label className="field">
        <span>Image link</span>
        <CommittedInput
          value={recipe.imageUrl}
          placeholder="https://…"
          onCommit={(imageUrl) => void mutate((s) => updateRecipe(s, recipe.id, { imageUrl }))}
        />
      </label>

      <button type="button" className="btn block" style={{ marginTop: 8 }} onClick={onClose}>
        Done
      </button>
    </Sheet>
  )
}

/**
 * Fetching goes through /api/scrape because a browser cannot fetch another origin. Recipe
 * pages nearly always carry a JSON-LD Recipe block or Open Graph tags, which is where the
 * photo and the summary come from.
 */
function RecipeSheet({ onClose }: { onClose: () => void }) {
  const { mutate, showToast } = useStore()
  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [fetching, setFetching] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  async function fetchDetails() {
    if (!safeUrl(url)) {
      setNote('Enter an https link first')
      return
    }
    setFetching(true)
    setNote(null)
    try {
      const response = await fetch(`/api/scrape?url=${encodeURIComponent(url.trim())}`)
      const data = (await response.json()) as ScrapeResponse
      if (data.title) setTitle(data.title)
      if (data.description) setDescription(data.description)
      if (data.imageUrl) setImageUrl(data.imageUrl)
      // The scraper's warnings are written for the budget flow and mostly concern prices,
      // which a recipe does not have. Only the outcome that matters here is reported.
      if (!data.title && !data.imageUrl) {
        setNote('Nothing came back from that page — fill it in below')
      }
    } catch {
      setNote('Could not reach that page — fill it in below')
    } finally {
      setFetching(false)
    }
  }

  return (
    <Sheet onClose={onClose}>
      <h2 style={{ margin: '0 0 14px', fontSize: 20 }}>Add a recipe</h2>

      <label className="field">
        <span>Link</span>
        <div className="row">
          <input
            type="url"
            inputMode="url"
            autoFocus
            placeholder="https://…"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value)
              setNote(null)
            }}
            style={{ flex: 1 }}
          />
          <button type="button" className="btn" disabled={fetching} onClick={fetchDetails}>
            {fetching ? '…' : 'Fetch'}
          </button>
        </div>
      </label>

      {note ? (
        <div className="muted" style={{ fontSize: 13, marginTop: -6, marginBottom: 12 }}>
          {note}
        </div>
      ) : null}

      {imageUrl ? (
        <img
          className="recipe-image"
          style={{ borderRadius: 12, marginBottom: 14 }}
          src={imageUrl}
          alt=""
          onError={() => setImageUrl('')}
        />
      ) : null}

      <label className="field">
        <span>Title</span>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>

      <label className="field">
        <span>Description</span>
        <textarea
          value={description}
          maxLength={MAX_DESCRIPTION}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <label className="field">
        <span>Image link</span>
        <input
          type="url"
          inputMode="url"
          placeholder="https://…"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
        />
      </label>

      <div className="row" style={{ marginTop: 8 }}>
        <button
          type="button"
          className="btn grow"
          disabled={title.trim() === ''}
          onClick={async () => {
            const saved = await mutate((s) => addRecipe(s, { title, description, url, imageUrl }))
            if (saved) {
              onClose()
              showToast(`Added "${title.trim()}"`)
            }
          }}
        >
          Add recipe
        </button>
        <button type="button" className="btn ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  )
}
